import isEqual from "lodash/isEqual";
import throttle from "lodash/throttle";
import LogRocket from "logrocket";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import thunk from "redux-thunk";
import { getSizeInBytesForString } from "./Helper";
import {
    loadLocalStorageState,
    loadSessionStorageState,
    saveLocalStorageState,
    saveSessionStorageState,
} from "./localStorage";
import { getSentryBreadcrumbMiddlewareForRedux } from "./Sentry";
import * as types from "./store/actions/types";
import accountReducer, { ACCOUNT_INITIAL_STATE } from "./store/reducers/account";
import availableFiltersReducer from "./store/reducers/availableFilters";
import axiosStatusReducer from "./store/reducers/axiosStatus";
import checkoutCartReducer from "./store/reducers/checkoutCart";
import clientDataReducer from "./store/reducers/clientData";
import filterReducer from "./store/reducers/filter";
import guestsReducer from "./store/reducers/guests";
import notificationsReducer from "./store/reducers/notifications";
import reservationReducer from "./store/reducers/reservation";
import reservationResultReducer from "./store/reducers/reservationResult";
import searchReducer from "./store/reducers/search";
import windowReducer from "./store/reducers/window";
import { isJwtTokenExpired } from "./BusinessUtilsTypescript";

const doNotLogTheseReduxActions = [
    types.SET_DEVICE,
    types.SCREEN_RESIZE,
    types.SET_LOADING,
    types.SET_REQUESTS_LOADING_BY_RESVTYPE,
    types.SET_REQUESTS_LOADING,
    types.CREATE_TIMESTAMP,
    types.SET_IFRAME_OFFSET_TOP,
    "SET_ERROR",
    types.SET_CUSTOMER_TOKEN,
];

const sanitizeStateForStorage = state => {
    const localStorageState = {
        account: {
            user: state.account.user,
            token: state.account.token,
            loginSuccessful: state.account.loginSuccessful,
        },
        checkoutCart: state.checkoutCart,
    };

    const sessionStorageState = {
        clientData: state.clientData,
        filter: state.filter,
        guests: state.guests,
        reservationResult: state.reservationResult,
        search: state.search,
    };

    const sessionStorageStateString = JSON.stringify(sessionStorageState);
    const sessionStorageSizeInMB = getSizeInBytesForString(sessionStorageStateString) / 1024 / 1024;

    if (sessionStorageSizeInMB > 2) {
        // If the session storage is too large, optimize it by reducing the reservationResult to only the search property.
        sessionStorageState.reservationResult = Object.fromEntries(
            Object.entries(state.reservationResult || {}).map(([resvTypeLid, data]) => [
                resvTypeLid,
                { search: data.search },
            ])
        );

        // Also, optimize the search state by reducing the lastSearched property to an empty object.
        sessionStorageState.search = { ...state.search, lastSearched: {} };
    }

    return {
        localStorage: localStorageState,
        sessionStorage: sessionStorageState,
    };
};

export const syncStateToBrowserStorage = store => {
    return () => {
        const previousLocalStorageState = loadLocalStorageState();
        const previousSessionStorageState = loadSessionStorageState();

        const sanitizedState = sanitizeStateForStorage(store.getState());

        const statesAreEqual =
            isEqual(previousLocalStorageState, sanitizedState.localStorage) &&
            isEqual(previousSessionStorageState, sanitizedState.sessionStorage);

        // NOTE: States between two tabs are not always equal even if may look like it at a first glance.
        // E.g. the sorting of externalProducts in clientData are randomized if the sort_order is the same, which may be different between tabs.
        if (statesAreEqual) {
            return;
        }

        saveLocalStorageState(sanitizedState.localStorage);
        saveSessionStorageState(sanitizedState.sessionStorage);
    };
};

const rootReducer = (state, action) => {
    if (action.type === types.DESTROY_STATE) {
        const { clientData, window, filter, availableFilters, account, axiosStatus } = state;

        // Returning appReducer() ensures that the state uses initial values for undefined properties. E.g. for checkoutCart.
        return appReducer({ clientData, window, filter, availableFilters, account, axiosStatus }, action);
    }

    if (action.type === types.HYDRATE_STATE) {
        if (!action.payload.account || !action.payload.checkoutCart) {
            return state;
        }

        // Keep current state and only update account and checkoutCart.
        return { ...state, account: action.payload.account, checkoutCart: action.payload.checkoutCart };
    }

    return appReducer(state, action);
};

const appReducer = combineReducers({
    account: accountReducer,
    availableFilters: availableFiltersReducer,
    axiosStatus: axiosStatusReducer,
    checkoutCart: checkoutCartReducer,
    clientData: clientDataReducer,
    filter: filterReducer,
    guests: guestsReducer,
    notifications: notificationsReducer,
    reservation: reservationReducer,
    reservationResult: reservationResultReducer,
    search: searchReducer,
    window: windowReducer,
});

const configureStore = () => {
    const reduxDevToolsConfig = { trace: true, maxAge: 200 };
    const composeEnhancers =
        typeof window === "object" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
            ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__(reduxDevToolsConfig)
            : compose;

    const reduxMiddlewareConfig = {
        actionSanitizer: action => (doNotLogTheseReduxActions.includes(action.type) ? null : action),
    };

    const enhancer = composeEnhancers(
        applyMiddleware(thunk),
        applyMiddleware(LogRocket.reduxMiddleware(reduxMiddlewareConfig)),
        applyMiddleware(getSentryBreadcrumbMiddlewareForRedux(doNotLogTheseReduxActions))
    );

    let initialLocalStorageState = loadLocalStorageState();
    let initialSessionStorageState = loadSessionStorageState();

    // If logged in, merge the saved token with the initial account state.
    if (
        initialLocalStorageState?.account?.token &&
        initialLocalStorageState?.account?.user &&
        initialLocalStorageState?.account?.loginSuccessful
    ) {
        // Verify that the token is not expired.
        if (isJwtTokenExpired(initialLocalStorageState.account.token)) {
            // If user was logged in as agent or owner, clear the cart.
            if (initialLocalStorageState.account.user?.isAgent || initialLocalStorageState.account.user?.isOwner) {
                delete initialLocalStorageState.checkoutCart;
            }

            delete initialLocalStorageState.account;
        } else {
            initialLocalStorageState.account = {
                ...ACCOUNT_INITIAL_STATE,
                user: initialLocalStorageState.account.user,
                token: initialLocalStorageState.account.token,
                loginSuccessful: initialLocalStorageState.account.loginSuccessful,
            };
        }
    } else if (initialLocalStorageState?.account) {
        // If not logged in, remove the account state to use the initial state.
        delete initialLocalStorageState.account;
    }

    const store = createStore(rootReducer, { ...initialLocalStorageState, ...initialSessionStorageState }, enhancer);
    store.subscribe(throttle(syncStateToBrowserStorage(store), 2000));

    return store;
};

const store = configureStore();

export default store;
